'use client'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/utilities/ui'

import DriftingBlob from './DriftingBlob'

/** Master list of anchor positions (%). Blobs pick the next target at random when they arrive.
 * Stratified across the box: corners, edge mids, quadrant interiors, and center-adjacent points
 * so vertical/horizontal bands stay balanced (avoids clustering along the top strip). */
const BLOB_POSITION_POOL: readonly { left: string; top: string }[] = [
  { left: '10%', top: '10%' },
  { left: '90%', top: '12%' },
  { left: '12%', top: '88%' },
  { left: '88%', top: '86%' },
  { left: '50%', top: '8%' },
  { left: '50%', top: '92%' },
  { left: '8%', top: '50%' },
  { left: '92%', top: '48%' },
  { left: '30%', top: '32%' },
  { left: '70%', top: '34%' },
  { left: '28%', top: '68%' },
  { left: '72%', top: '66%' },
  { left: '50%', top: '50%' },
  { left: '38%', top: '52%' },
  { left: '62%', top: '48%' },
  { left: '48%', top: '38%' },
  { left: '52%', top: '62%' },
] as const

type LiveGradientBackgroundProps = {
  colorA: string
  colorB: string
  className?: string
  /** Optional override for the position pool (defaults to BLOB_POSITION_POOL) */
  positionPool?: readonly { left: string; top: string }[]
  moveDurationBaseMs?: number
  /** Larger values make duration scaling vs container size gentler (reference max side in px) */
  durationScaleReferencePx?: number
}

const LiveGradientBackground = ({
  colorA,
  colorB,
  moveDurationBaseMs = 25000,
  durationScaleReferencePx = 500,
  className = '',
  positionPool: _positionPool,
}: LiveGradientBackgroundProps) => {
  const [reduceMotion, setReduceMotion] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerMaxSidePx, setContainerMaxSidePx] = useState(0)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduceMotion(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  const pool = useMemo(
    () => (_positionPool && _positionPool.length > 0 ? _positionPool : BLOB_POSITION_POOL),
    [_positionPool],
  )

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const { width, height } = el.getBoundingClientRect()
      setContainerMaxSidePx(Math.max(width, height))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      ref={containerRef}
      className={cn('absolute inset-0 overflow-hidden blur-[80px] contain-paint', className)}
      aria-hidden
    >
      <div className="bg-morph-layer-a absolute inset-0">
        {Array.from({ length: 4 }).map((_, i) => (
          <DriftingBlob
            key={`a-${i}`}
            pool={pool}
            color={colorA}
            seedIndex={i * 2}
            reduceMotion={reduceMotion}
            moveDurationBaseMs={moveDurationBaseMs}
            containerMaxSidePx={containerMaxSidePx}
            durationScaleReferencePx={durationScaleReferencePx}
          />
        ))}
      </div>
      <div className="bg-morph-layer-b absolute inset-0">
        {Array.from({ length: 4 }).map((_, i) => (
          <DriftingBlob
            key={`b-${i}`}
            pool={pool}
            color={colorB}
            seedIndex={i * 2 + 1}
            reduceMotion={reduceMotion}
            moveDurationBaseMs={moveDurationBaseMs}
            containerMaxSidePx={containerMaxSidePx}
            durationScaleReferencePx={durationScaleReferencePx}
          />
        ))}
      </div>
    </div>
  )
}

export default LiveGradientBackground
